import { useState, useEffect, useMemo } from 'react';
import { Loader2, Trash2, RefreshCw, Play, Pause, Plus, ListPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';


interface QueueRow { id: string; keyword: string; status: string; error: string | null; created_at: string; published_at: string | null; }
interface ScheduledRow { id: string; keyword: string; status: string; error: string | null; scheduled_for: string; }

const statusVariant = (s: string) =>
  s === 'published' || s === 'done' ? 'default' : s === 'failed' ? 'destructive' : 'outline';

const AdminBlogQueue = () => {
  const { toast } = useToast();
  const [queue, setQueue] = useState<QueueRow[]>([]);
  const [scheduled, setScheduled] = useState<ScheduledRow[]>([]);
  const [cronEnabled, setCronEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [bulk, setBulk] = useState('');
  const [adding, setAdding] = useState(false);

  const keywordsToAdd = useMemo(
    () => bulk.split('\n').map(k => k.trim()).filter(Boolean),
    [bulk],
  );

  const counts = useMemo(() => ({
    pending: queue.filter(q => q.status === 'pending').length,
    published: queue.filter(q => q.status === 'published' || q.status === 'done').length,
    failed: queue.filter(q => q.status === 'failed').length,
  }), [queue]);


  const load = async () => {
    const [q, s, st] = await Promise.all([
      supabase.from('blog_queue').select('id, keyword, status, error, created_at, published_at').order('created_at', { ascending: false }),
      supabase.from('blog_scheduled_posts').select('id, keyword, status, error, scheduled_for').order('scheduled_for', { ascending: true }),
      supabase.from('blog_settings').select('cron_enabled').eq('id', 1).maybeSingle(),
    ]);
    setQueue(q.data || []);
    setScheduled(s.data || []);
    setCronEnabled(Boolean(st.data?.cron_enabled));
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggleCron = async (value: boolean) => {
    const { error } = await supabase.from('blog_settings').update({ cron_enabled: value }).eq('id', 1);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    setCronEnabled(value);
    toast({ title: value ? 'Auto-posting enabled' : 'Auto-posting paused' });
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke('autoblog-process', { body: {} });
      if (error) throw error;
      toast({ title: 'Processor run complete', description: `${data?.processed ?? 0} item(s) processed` });
      load();
    } catch (e) {
      toast({ title: 'Run failed', description: (e as Error).message, variant: 'destructive' });
    } finally { setRunning(false); }
  };

  const removeQueue = async (id: string) => {
    await supabase.from('blog_queue').delete().eq('id', id);
    load();
  };
  const retryQueue = async (id: string) => {
    await supabase.from('blog_queue').update({ status: 'pending', error: null }).eq('id', id);
    load();
  };
  const removeScheduled = async (id: string) => {
    await supabase.from('blog_scheduled_posts').delete().eq('id', id);
    load();
  };
  const retryScheduled = async (id: string) => {
    await supabase.from('blog_scheduled_posts').update({ status: 'pending', error: null, scheduled_for: new Date().toISOString() }).eq('id', id);
    load();
  };

  const addKeywords = async () => {
    if (!keywordsToAdd.length) return;
    setAdding(true);

    // Dedupe within the pasted list (case-insensitive) and against what already exists.
    const [{ data: existingQueue }, { data: existingScheduled }] = await Promise.all([
      supabase.from('blog_queue').select('keyword'),
      supabase.from('blog_scheduled_posts').select('keyword'),
    ]);
    const seen = new Set<string>([
      ...(existingQueue ?? []).map(r => r.keyword.trim().toLowerCase()),
      ...(existingScheduled ?? []).map(r => r.keyword.trim().toLowerCase()),
    ]);

    const unique: string[] = [];
    for (const keyword of keywordsToAdd) {
      const key = keyword.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(keyword);
    }
    const skipped = keywordsToAdd.length - unique.length;

    if (!unique.length) {
      setAdding(false);
      return toast({ title: 'No new keywords', description: 'All pasted keywords are already queued or scheduled.' });
    }

    const { error } = await supabase.from('blog_queue').insert(
      unique.map(keyword => ({ keyword, status: 'pending' })),
    );
    setAdding(false);
    if (error) return toast({ title: 'Error', description: error.message, variant: 'destructive' });
    toast({
      title: `${unique.length} keyword(s) added to queue`,
      description: skipped ? `${skipped} duplicate(s) skipped` : undefined,
    });
    setBulk('');
    load();
  };


  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Queue &amp; Scheduler</h1>
          <p className="text-muted-foreground mt-1">Bulk-add keywords. Queued posts auto-publish on schedule.</p>
        </div>

        <Card className="bg-foreground text-background border-transparent">
          <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${cronEnabled ? 'bg-emerald-400' : 'bg-background/40'}`} />
                Auto-Post Scheduler
                <Badge className={cronEnabled ? 'bg-emerald-500 text-white hover:bg-emerald-500' : 'bg-background/20 text-background hover:bg-background/20'}>
                  {cronEnabled ? 'ACTIVE' : 'PAUSED'}
                </Badge>
              </CardTitle>
              <CardDescription className="text-background/70 mt-1">
                Publishing the next queued keyword automatically · {counts.pending} keyword(s) remaining
              </CardDescription>
              <p className="font-mono text-xs text-background/60 mt-1">Schedule: processor runs every 3 days at 9:00am</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="secondary" size="sm" onClick={runNow} disabled={running}>
                {running ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}Run now
              </Button>
              <Button variant="secondary" size="sm" onClick={() => toggleCron(!cronEnabled)}>
                {cronEnabled ? <Pause className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
                {cronEnabled ? 'Pause' : 'Resume'}
              </Button>
            </div>
          </CardHeader>
        </Card>

        <div className="grid gap-4 sm:grid-cols-3">
          {[
            { label: 'Pending', value: counts.pending, cls: 'text-foreground' },
            { label: 'Published', value: counts.published, cls: 'text-emerald-600' },
            { label: 'Failed', value: counts.failed, cls: 'text-destructive' },
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="py-6 text-center">
                <p className={`text-4xl font-bold ${s.cls}`}>{s.value}</p>
                <p className="text-muted-foreground mt-1">{s.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="flex-row items-center gap-2 space-y-0">
            <ListPlus className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Add Keywords to Queue</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              rows={6}
              className="font-mono text-sm"
              placeholder={"ducted heating installation Bowral\nsplit system repair Mittagong\nheat pump servicing Moss Vale\n...one keyword per line"}
              value={bulk}
              onChange={e => setBulk(e.target.value)}
            />
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{keywordsToAdd.length} keywords ready to add</p>
              <Button onClick={addKeywords} disabled={!keywordsToAdd.length || adding}>
                {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}Add to Queue
              </Button>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <Tabs defaultValue="queue">

            <TabsList>
              <TabsTrigger value="scheduled">Scheduled ({scheduled.length})</TabsTrigger>
              <TabsTrigger value="queue">Keyword queue ({queue.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="scheduled">
              <Card><CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Keyword</TableHead><TableHead>Scheduled for</TableHead><TableHead>Status</TableHead><TableHead className="w-28" /></TableRow></TableHeader>
                  <TableBody>
                    {scheduled.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nothing scheduled</TableCell></TableRow>}
                    {scheduled.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.keyword}{r.error && <p className="text-xs text-destructive mt-1">{r.error}</p>}</TableCell>
                        <TableCell>{new Date(r.scheduled_for).toLocaleString('en-AU')}</TableCell>
                        <TableCell><Badge variant={statusVariant(r.status)}>{r.status}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => retryScheduled(r.id)}><RefreshCw className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => removeScheduled(r.id)}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent></Card>
            </TabsContent>

            <TabsContent value="queue">
              <Card><CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow><TableHead>Keyword</TableHead><TableHead>Added</TableHead><TableHead>Status</TableHead><TableHead className="w-28" /></TableRow></TableHeader>
                  <TableBody>
                    {queue.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Queue is empty</TableCell></TableRow>}
                    {queue.map(r => (
                      <TableRow key={r.id}>
                        <TableCell className="font-medium">{r.keyword}{r.error && <p className="text-xs text-destructive mt-1">{r.error}</p>}</TableCell>
                        <TableCell>{new Date(r.created_at).toLocaleDateString('en-AU')}</TableCell>
                        <TableCell><Badge variant={statusVariant(r.status)}>{r.status}</Badge></TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => retryQueue(r.id)}><RefreshCw className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => removeQueue(r.id)}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent></Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBlogQueue;
